import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import { useTranslation } from 'react-i18next';
import { GroupStats } from '../../../../shared/models/agency-stats/AgencyStatsResponse';
import { getChartThemeColors } from '../../../../shared/utils/chartTheme';
import { useTheme } from '../../../../shared/contexts/theme/ThemeContext';

const CHART_COLOR_ACTIVE = '#40b681';   // --green-100
const CHART_COLOR_PAUSED = '#4c7dea';   // --primary-ux-400
const CHART_COLOR_PENDING = '#e0c951';  // --yellow-200
const CHART_COLOR_DELETED = '#959596';  // --grey-ux-500

interface AgencyStatsBarChartProps {
  groups: GroupStats[];
}

const AgencyStatsBarChart: React.FC<AgencyStatsBarChartProps> = ({ groups }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const barOption = useMemo<EChartsOption>(() => {
    const labels = groups.map((g) => (g.groupId === null ? t('ungrouped') : g.groupName));
    const chartColors = getChartThemeColors();

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'none' },
        appendToBody: true,
        borderColor: chartColors.tooltipBorder,
        backgroundColor: chartColors.tooltipBg,
        textStyle: { color: chartColors.tooltipText },
      },
      legend: { show: false },
      grid: { top: 24, bottom: 40, left: 40, right: 16 },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: {
          fontFamily: 'Inter,sans-serif',
          fontSize: 11,
          interval: 0,
          rotate: labels.length > 6 ? 30 : 0,
          overflow: 'truncate',
          width: 80,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { color: chartColors.splitLine } },
        minInterval: 1,
      },
      series: [
        {
          name: t('agencyStatsActive'),
          type: 'bar',
          data: groups.map((g) => g.activePrompts),
          itemStyle: { color: CHART_COLOR_ACTIVE, borderRadius: [2, 2, 0, 0] },
          barMaxWidth: 32,
        },
        {
          name: t('agencyStatsPaused'),
          type: 'bar',
          data: groups.map((g) => g.pausedPrompts),
          itemStyle: { color: CHART_COLOR_PAUSED, borderRadius: [2, 2, 0, 0] },
          barMaxWidth: 32,
        },
        {
          name: t('agencyStatsPending'),
          type: 'bar',
          data: groups.map((g) => g.pendingPrompts),
          itemStyle: { color: CHART_COLOR_PENDING, borderRadius: [2, 2, 0, 0] },
          barMaxWidth: 32,
        },
        {
          name: t('agencyStatsDeletedPrompts'),
          type: 'bar',
          data: groups.map((g) => g.deletedPrompts),
          itemStyle: { color: CHART_COLOR_DELETED, borderRadius: [2, 2, 0, 0] },
          barMaxWidth: 32,
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, t, theme]);

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('agencyStatsPromptsPerGroup')}
        </div>
      </div>
      <div className="stats-box stats-box--chart">
        <div className="stats-box__content">
          <ReactECharts key={theme} option={barOption} style={{ height: '294px', width: '100%' }} notMerge />
        </div>
      </div>
    </div>
  );
};

export default AgencyStatsBarChart;
